import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "./api";

/** Excel/TSV metnini 2 boyutlu sayı tablosuna çevirir. Boş satır/kolonlar atılır. */
export function parseGrid(text) {
  const rows = String(text || "")
    .replace(/\r/g, "")
    .split("\n")
    .map((line) => line.split("\t"))
    .filter((cells) => cells.some((c) => c.trim() !== ""));
  return rows.map((cells) => cells.map(toNumber));
}

/** "1.234,56" / "1,234.56" / "1 234" / "€ 980" hepsini sayıya çevirir. */
export function toNumber(raw) {
  let s = String(raw ?? "").replace(/[^\d.,-]/g, "").trim();
  if (!s) return 0;
  const lastDot = s.lastIndexOf(".");
  const lastComma = s.lastIndexOf(",");
  if (lastDot > -1 && lastComma > -1) {
    const dec = lastDot > lastComma ? "." : ",";
    s = dec === "." ? s.replace(/,/g, "") : s.replace(/\./g, "").replace(",", ".");
  } else if (lastComma > -1) {
    s = s.length - lastComma === 4 ? s.replace(/,/g, "") : s.replace(",", ".");
  } else if (lastDot > -1) {
    s = s.length - lastDot === 4 ? s.replace(/\./g, "") : s;
  }
  const v = Number(s);
  return Number.isFinite(v) ? v : 0;
}

/** "100, 125, 150" veya "100-300/25" (başlangıç-bitiş/adım) → sayı listesi */
export function parseAxis(text) {
  const s = String(text || "").trim();
  const rng = s.match(/^(-?\d+(?:[.,]\d+)?)\s*-\s*(-?\d+(?:[.,]\d+)?)\s*\/\s*(\d+(?:[.,]\d+)?)$/);
  if (rng) {
    const [a, b, st] = [toNumber(rng[1]), toNumber(rng[2]), toNumber(rng[3])];
    const out = [];
    if (st > 0) for (let v = a; v <= b + 1e-9; v += st) out.push(Math.round(v * 100) / 100);
    return out;
  }
  return s.split(/[,;\s]+/).filter(Boolean).map(toNumber);
}

export function gridToTsv(prices) {
  return (prices || []).map((r) => r.join("\t")).join("\n");
}

/** Mevcut ekseni referans alıp n uzunluğunda aritmetik ölçü listesi önerir. */
export function suggestAxis(axis, n) {
  const start = axis?.[0] ?? 100;
  const step = axis && axis.length > 1 ? axis[1] - axis[0] : 25;
  return Array.from({ length: n }, (_, i) => Math.round((start + i * step) * 100) / 100);
}

export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  }
}

const Shell = ({ title, hint, children, onClose, testid }) => (
  <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-6" data-testid={testid}>
    <div className="zen-panel w-full max-w-3xl p-5 space-y-4 mt-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-extrabold tracking-tight">{title}</h3>
          {hint && <p className="text-xs text-slate-500 mt-1 leading-relaxed">{hint}</p>}
        </div>
        <button onClick={onClose} className="zen-btn-ghost !px-3 !py-1 text-lg leading-none" data-testid="matrix-modal-close">×</button>
      </div>
      {children}
    </div>
  </div>
);

const AxisFields = ({ w, h, setW, setH }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">En ölçüleri (kolonlar, cm)</label>
      <input className="zen-input !py-2 w-full mt-1" value={w} onChange={(e) => setW(e.target.value)}
             placeholder="100-300/25 veya 100, 125, 150" data-testid="matrix-widths-input" />
    </div>
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Boy ölçüleri (satırlar, cm)</label>
      <input className="zen-input !py-2 w-full mt-1" value={h} onChange={(e) => setH(e.target.value)}
             placeholder="100-300/25 veya 100, 125, 150" data-testid="matrix-heights-input" />
    </div>
  </div>
);

const Preview = ({ grid }) => {
  if (!grid.length) return null;
  const rows = grid.slice(0, 6);
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-[#22304A]">
      <table className="text-[11px] zen-mono w-full" data-testid="matrix-paste-preview">
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100 dark:border-[#1A2438] last:border-0">
              {r.slice(0, 12).map((v, j) => (
                <td key={j} className="px-2 py-1 text-right whitespace-nowrap">{v}</td>
              ))}
              {r.length > 12 && <td className="px-2 py-1 text-slate-400">…</td>}
            </tr>
          ))}
          {grid.length > 6 && (
            <tr><td className="px-2 py-1 text-slate-400" colSpan={13}>… {grid.length - 6} satır daha</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

/** Mevcut matrise Excel'den fiyat bloğu yapıştırma */
export function MatrixPasteModal({ matrix, onClose, onSaved }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const grid = useMemo(() => parseGrid(text), [text]);
  const nRow = grid.length;
  const nCol = nRow ? Math.max(...grid.map((r) => r.length)) : 0;
  const fits = nRow === matrix.heights.length && nCol === matrix.widths.length;
  const [wTxt, setWTxt] = useState(matrix.widths.join(", "));
  const [hTxt, setHTxt] = useState(matrix.heights.join(", "));
  const widths = parseAxis(wTxt);
  const heights = parseAxis(hTxt);
  const axisOk = widths.length === nCol && heights.length === nRow;

  // Boyut değişince ölçü listelerini mevcut eksenin adımıyla otomatik öner
  useEffect(() => {
    if (!nRow || fits) return;
    setWTxt(suggestAxis(matrix.widths, nCol).join(", "));
    setHTxt(suggestAxis(matrix.heights, nRow).join(", "));
  }, [nRow, nCol, fits, matrix.widths, matrix.heights]);

  const save = async () => {
    if (!nRow) { toast.error("Önce Excel'den fiyat bloğunu yapıştırın"); return; }
    if (grid.some((r) => r.length !== nCol)) { toast.error("Satırlardaki kolon sayıları eşit değil"); return; }
    if (!fits && !axisOk) {
      toast.error(`Ölçü listeleri tabloya uymuyor: ${nRow} satır × ${nCol} kolon gerekiyor`);
      return;
    }
    setBusy(true);
    try {
      const body = { prices: grid };
      if (!fits) { body.widths = widths; body.heights = heights; }
      const { data } = await api.post(`/matrices/${matrix.id}/bulk`, body);      toast.success(`${nRow} × ${nCol} fiyat kaydedildi`);
      onSaved(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally { setBusy(false); }
  };

  return (
    <Shell
      testid="matrix-paste-modal"
      title={`Excel'den Yapıştır — ${matrix.name}`}
      hint="Excel'de SADECE fiyat hücrelerini seçip kopyalayın (başlık satırı/kolonu olmadan) ve aşağıya yapıştırın. Satırlar = Boy, kolonlar = En."
      onClose={onClose}
    >
      <textarea
        data-testid="matrix-paste-textarea"
        className="zen-input w-full h-40 font-mono text-[11px]"
        placeholder="Excel'den kopyaladığınız hücreleri buraya yapıştırın (Ctrl+V)"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="flex items-center gap-3 text-xs">
        <span className="font-bold" data-testid="matrix-paste-size">{nRow} satır × {nCol} kolon</span>
        <span className={fits ? "text-emerald-600 font-semibold" : "text-amber-600 font-semibold"} data-testid="matrix-paste-status">
          {nRow === 0 ? "Bekleniyor" : fits
            ? "Mevcut matris ölçüleriyle birebir uyuyor"
            : `Mevcut matris ${matrix.heights.length} × ${matrix.widths.length} — ölçü listelerini güncelleyin`}
        </span>
      </div>
      <Preview grid={grid} />
      {!fits && nRow > 0 && <AxisFields w={wTxt} h={hTxt} setW={setWTxt} setH={setHTxt} />}
      {!fits && nRow > 0 && (
        <div className="text-[11px] text-slate-500" data-testid="matrix-axis-info">
          En: {widths.length} değer · Boy: {heights.length} değer {axisOk ? "✓" : "— tabloyla eşleşmiyor"}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="zen-btn-ghost">İptal</button>
        <button onClick={save} disabled={busy || !nRow} className="zen-btn-primary" data-testid="matrix-paste-save">
          {busy ? "Kaydediliyor…" : "Fiyatları Kaydet"}
        </button>
      </div>
    </Shell>
  );
}

const KIND_LABEL = { cell: "Hücre düzenleme", bulk: "Excel yapıştırma", raise: "Toplu zam/indirim", restore: "Geri yükleme" };

/** Fiyat değişim geçmişi + istenen ana geri dönme */
export function MatrixHistoryModal({ matrix, onClose, onRestored }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => {
    api.get(`/matrices/${matrix.id}/history`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [matrix.id]);

  const restore = async (h) => {
    if (!window.confirm(`${fmtDate(h.createdAt)} öncesindeki fiyatlara dönülecek. Onaylıyor musunuz?`)) return;
    setBusy(h.id);
    try {
      const { data } = await api.post(`/matrices/${matrix.id}/history/${h.id}/restore`);
      toast.success("Eski fiyatlar geri yüklendi");
      onRestored(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Geri yüklenemedi");
    } finally { setBusy(""); }
  };

  return (
    <Shell
      testid="matrix-history-modal"
      title={`Fiyat Değişim Geçmişi — ${matrix.name}`}
      hint="Her satır, o değişiklikten ÖNCEKİ fiyat tablosunu saklar. 'Geri Yükle' dediğinizde matris o hâline döner (mevcut hâl de geçmişe eklenir)."
      onClose={onClose}
    >
      {rows === null && <div className="text-xs text-slate-500">Yükleniyor…</div>}
      {rows?.length === 0 && <div className="text-xs text-slate-500" data-testid="matrix-history-empty">Bu matriste henüz kayıtlı değişiklik yok.</div>}
      {rows?.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-[#22304A]">
          <table className="w-full text-xs" data-testid="matrix-history-table">
            <thead>
              <tr className="bg-slate-50 dark:bg-[#0C1220] text-left">
                <th className="px-3 py-2 font-bold">Tarih</th>
                <th className="px-3 py-2 font-bold">Kullanıcı</th>
                <th className="px-3 py-2 font-bold">İşlem</th>
                <th className="px-3 py-2 font-bold">Açıklama</th>
                <th className="px-3 py-2 font-bold text-right">Hücre</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((h) => (
                <tr key={h.id} className="border-t border-slate-100 dark:border-[#1A2438]" data-testid={`matrix-history-row-${h.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap zen-mono">{fmtDate(h.createdAt)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{h.userName || h.userEmail}</td>
                  <td className="px-3 py-2 whitespace-nowrap font-semibold">{KIND_LABEL[h.kind] || h.kind}</td>
                  <td className="px-3 py-2 text-slate-500">{h.note}</td>
                  <td className="px-3 py-2 text-right zen-mono">{h.changedCells || 0}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => restore(h)} disabled={busy === h.id}
                            className="zen-btn-ghost !py-1 !px-2 !text-[11px]" data-testid={`matrix-history-restore-${h.id}`}>
                      {busy === h.id ? "…" : "Geri Yükle"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex justify-end">
        <button onClick={onClose} className="zen-btn-ghost">Kapat</button>
      </div>
    </Shell>
  );
}

function fmtDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

/** Sipariş formunda fiyatın hangi matris/hücreden geldiğini gösteren açılır pencere */
export function MatrixTraceModal({ info, onClose }) {
  const t = info?.trace;
  return (
    <Shell
      testid="matrix-trace-modal"
      title="Fiyat Nereden Geldi?"
      hint="Bu bilgi yalnızca ekranda gösterilir, teklif/PDF çıktısına yazılmaz."
      onClose={onClose}
    >
      <div className="space-y-2 text-xs">
        <Row k="Matris" v={info.matrixName || "—"} testid="trace-matrix-name" />
        <Row k="Kaynak" v={info.source === "db" ? "Matris ekranı (canlı veri)" : "PDF'ten üretilen yedek dosya"} testid="trace-source" />
        <Row k="Model / Kumaş ailesi" v={`${info.model} · ${info.family}`} />
        <Row k="Girilen ölçü" v={`En ${info.wCm} cm × Boy ${info.hCm} cm`} />
        {t && (
          <>
            <Row k="Kullanılan hücre (yukarı yuvarlanmış)" v={`En ${t.w} cm × Boy ${t.h} cm`} testid="trace-range" />
            <Row k="Hesap" v="Girilen ölçü, matrisin bir üst basamağına yuvarlanır ve o hücrenin fiyatı doğrudan kullanılır (ara değer hesaplanmaz — basamak sabit fiyatlıdır)." testid="trace-method" />
          </>
        )}
        <Row k="Birim fiyat" v={`€ ${Number(info.price || 0).toLocaleString("tr-TR")}`} testid="trace-price" />
        {info.isRedZone && (
          <div className="text-[11px] font-semibold text-red-600 dark:text-red-400" data-testid="trace-redzone">
            Bu hücre PDF'te kırmızı işaretli — garanti kapsamı dışı.
          </div>
        )}
      </div>
      <div className="flex justify-end">
        <button onClick={onClose} className="zen-btn-ghost" data-testid="trace-close">Kapat</button>
      </div>
    </Shell>
  );
}

const Row = ({ k, v, testid }) => (
  <div className="flex gap-3">
    <span className="w-44 shrink-0 text-slate-500">{k}</span>
    <span className="font-semibold" data-testid={testid}>{v}</span>
  </div>
);

/** Seçili matrise yüzde zam / indirim uygular */
export function MatrixRaiseModal({ matrix, onClose, onSaved }) {
  const [pct, setPct] = useState("10");
  const [mode, setMode] = useState("round");
  const [onlyFilled, setOnlyFilled] = useState(true);
  const [busy, setBusy] = useState(false);
  const rate = toNumber(pct);
  const next = useMemo(() => {
    const f = 1 + rate / 100;
    return (matrix.prices || []).map((r) => r.map((v) => {
      if (onlyFilled && !v) return v;
      const nv = v * f;
      return mode === "round" ? Math.round(nv) : Math.round(nv * 100) / 100;
    }));
  }, [matrix.prices, rate, mode, onlyFilled]);

  const changed = next.reduce((acc, r, i) => acc + r.filter((v, j) => v !== matrix.prices[i][j]).length, 0);
  const apply = async () => {
    if (!rate) { toast.error("Sıfırdan farklı bir yüzde girin"); return; }
    if (!changed) { toast.error("Değişecek hücre yok"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/matrices/${matrix.id}/bulk`, {
        prices: next, kind: "raise",
        note: `Toplu ${rate > 0 ? "zam" : "indirim"} %${rate}${onlyFilled ? " (sadece dolu hücreler)" : ""}`,
      });
      toast.success(`%${rate} uygulandı — ${changed} hücre güncellendi`);
      onSaved(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally { setBusy(false); }
  };

  const rows = (matrix.prices || []).slice(0, 5);
  return (
    <Shell
      testid="matrix-raise-modal"
      title={`Toplu Zam / İndirim — ${matrix.name}`}
      hint="Pozitif değer zam, negatif değer indirim uygular. Aşağıdaki önizlemede eski → yeni fiyatları görebilirsiniz."
      onClose={onClose}
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Oran (%)</label>
          <input className="zen-input !py-2 w-full mt-1" value={pct} onChange={(e) => setPct(e.target.value)}
                 placeholder="örn. 10 veya -5" data-testid="matrix-raise-pct" />
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Yuvarlama</label>
          <select className="zen-input !py-2 w-full mt-1" value={mode} onChange={(e) => setMode(e.target.value)} data-testid="matrix-raise-round">
            <option value="round">Tam sayıya yuvarla</option>
            <option value="cent">Kuruş/sent (2 hane)</option>
          </select>
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300 pb-2">
            <input type="checkbox" checked={onlyFilled} onChange={(e) => setOnlyFilled(e.target.checked)} data-testid="matrix-raise-onlyfilled" />
            Sadece dolu hücreler
          </label>
        </div>
      </div>
      <div className="text-xs font-bold" data-testid="matrix-raise-count">{changed} hücre değişecek</div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-[#22304A]">
        <table className="text-[11px] zen-mono w-full" data-testid="matrix-raise-preview">
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-100 dark:border-[#1A2438] last:border-0">
                {r.slice(0, 8).map((v, j) => (
                  <td key={j} className="px-2 py-1 whitespace-nowrap text-right">
                    <span className="text-slate-400 line-through">{v}</span>{" "}
                    <strong className="text-emerald-600 dark:text-emerald-400">{next[i][j]}</strong>
                  </td>
                ))}
                {r.length > 8 && <td className="px-2 py-1 text-slate-400">…</td>}
              </tr>
            ))}
            {(matrix.prices || []).length > 5 && (
              <tr><td className="px-2 py-1 text-slate-400" colSpan={9}>… {matrix.prices.length - 5} satır daha</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="zen-btn-ghost">İptal</button>
        <button onClick={apply} disabled={busy} className="zen-btn-primary" data-testid="matrix-raise-save">
          {busy ? "Uygulanıyor…" : "Uygula ve Kaydet"}
        </button>
      </div>
    </Shell>
  );
}

/** Sıfırdan matris oluşturma (ölçü eksenleri + opsiyonel fiyat bloğu) */
export function NewMatrixModal({ groups, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [groupSlug, setGroupSlug] = useState(groups?.[0]?.slug || "");
  const [currency, setCurrency] = useState("EUR");
  const [wTxt, setWTxt] = useState("100-300/25");
  const [hTxt, setHTxt] = useState("100-300/25");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [artModel, setArtModel] = useState("");
  const [fabricFamily, setFabricFamily] = useState("");
  const [skyModel, setSkyModel] = useState("");
  const widths = parseAxis(wTxt);
  const heights = parseAxis(hTxt);
  const grid = useMemo(() => parseGrid(text), [text]);

  const create = async () => {
    if (!name.trim()) { toast.error("Matris adı zorunlu"); return; }
    if (!widths.length || !heights.length) { toast.error("En ve Boy ölçülerini girin"); return; }
    if (grid.length && (grid.length !== heights.length || grid.some((r) => r.length !== widths.length))) {
      toast.error(`Yapıştırılan tablo ${heights.length} satır × ${widths.length} kolon olmalı`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/matrices", {
        name: name.trim(), groupSlug, currency, widths, heights,
        artModel: artModel || undefined, skyModel: skyModel || undefined,
        fabricFamily: fabricFamily || undefined,
        prices: grid.length ? grid : undefined,
      });
      toast.success("Matris oluşturuldu");
      onCreated(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Oluşturulamadı");
    } finally { setBusy(false); }
  };

  return (
    <Shell
      testid="new-matrix-modal"
      title="Yeni Matris"
      hint="Ölçü eksenlerini girin. Fiyatları şimdi Excel'den yapıştırabilir ya da sonra doldurabilirsiniz."
      onClose={onClose}
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Matris Adı</label>
          <input className="zen-input !py-2 w-full mt-1" value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="örn. Sky 1600 Zip — Serge Ferrari" data-testid="new-matrix-name" />
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Para Birimi</label>
          <select className="zen-input !py-2 w-full mt-1" value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="new-matrix-currency">
            {["EUR", "TL", "USD"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Ürün Grubu</label>
        <select className="zen-input !py-2 w-full mt-1" value={groupSlug} onChange={(e) => setGroupSlug(e.target.value)} data-testid="new-matrix-group">
          {(groups || []).map((g) => <option key={g.slug} value={g.slug}>{g.name} ({g.slug})</option>)}
        </select>
      </div>
      <AxisFields w={wTxt} h={hTxt} setW={setWTxt} setH={setHTxt} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Dikey Zip Modeli (ops.)</label>
          <select className="zen-input !py-2 w-full mt-1" value={artModel} onChange={(e) => { setArtModel(e.target.value); if (e.target.value) setSkyModel(""); }} data-testid="new-matrix-artmodel">
            <option value="">— yok —</option>
            {["Art 90", "Art 110", "Art 130", "Art 140"].map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Sky Zip Modeli (ops.)</label>
          <select className="zen-input !py-2 w-full mt-1" value={skyModel} onChange={(e) => { setSkyModel(e.target.value); if (e.target.value) setArtModel(""); }} data-testid="new-matrix-skymodel">
            <option value="">— yok —</option>
            {["Sky 1300", "Sky 1400", "Sky 1500", "Sky 1600"].map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Kumaş Ailesi (ops.)</label>
          <select className="zen-input !py-2 w-full mt-1" value={fabricFamily} onChange={(e) => setFabricFamily(e.target.value)} data-testid="new-matrix-family">
            <option value="">— yok —</option>
            <option value="helio">Helio Screen</option>
            <option value="akrilik">Soltis 96 Akrilik (Sky)</option>
            <option value="serge">Serge Ferrari (86/92/96)</option>
            <option value="transparan">Transparan (Dikey Zip)</option>
          </select>
        </div>
      </div>
      <p className="text-[11px] text-slate-500">Model + kumaş ailesi seçilirse bu matris ilgili sipariş formunda otomatik kullanılır.</p>
      <div className="text-[11px] text-slate-500">{heights.length} satır (Boy) × {widths.length} kolon (En)</div>
      <textarea
        data-testid="new-matrix-paste"
        className="zen-input w-full h-28 font-mono text-[11px]"
        placeholder="(Opsiyonel) Excel'den fiyat bloğunu yapıştırın"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {grid.length > 0 && (
        <div className="text-[11px] font-bold" data-testid="new-matrix-paste-size">
          Yapıştırılan: {grid.length} × {Math.max(...grid.map((r) => r.length))}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="zen-btn-ghost">İptal</button>
        <button onClick={create} disabled={busy} className="zen-btn-primary" data-testid="new-matrix-save">
          {busy ? "Oluşturuluyor…" : "Matrisi Oluştur"}
        </button>
      </div>
    </Shell>
  );
}
